import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSession } from '@/context/SessionContext';
import { useLanguage } from '@/context/LanguageContext';
import LearnerLayout from '@/components/LearnerLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Calendar, Star, FolderOpen, Monitor, PenTool, Download, User,
  ChevronLeft, ChevronRight, Clock, Loader2, Video
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LearnerDashboard = () => {
  const { user } = useAuth();
  const { selectedSessionId, currentCourse } = useSession();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (!selectedSessionId) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API}/learner/dashboard/${selectedSessionId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setDashboard(data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedSessionId]);

  const monthNames = ['JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'];
  const dayNames = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay() || 7;
    const days = [];
    const prevLast = new Date(year, month, 0).getDate();
    for (let i = startingDay - 2; i >= 0; i--) days.push({ date: prevLast - i, current: false });
    for (let i = 1; i <= lastDay.getDate(); i++) days.push({ date: i, current: true });
    while (days.length < 35) days.push({ date: days.length - lastDay.getDate() - startingDay + 2, current: false });
    return days;
  };

  const sessionDates = (dashboard?.sessions || []).map(s => {
    const [y, m, d] = (s.date || '').split('-').map(Number);
    return new Date(y, m - 1, d).toDateString();
  });

  if (loading) {
    return (
      <LearnerLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </LearnerLayout>
    );
  }

  const overallProgress = dashboard?.overall_progress || 0;

  return (
    <LearnerLayout>
      <div className="grid lg:grid-cols-3 gap-8" data-testid="learner-dashboard">
        {/* ═══ MAIN CONTENT (2 cols) ═══ */}
        <div className="lg:col-span-2 space-y-6">
          {/* Session Title */}
          <div className="text-center py-6" data-testid="session-title">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 leading-snug">
              {dashboard?.course?.title || currentCourse?.title || 'Formation'}
            </h1>
          </div>

          {/* AFFEA Accessibility Commitment Card */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="text-center p-8 pb-4">
              <h2 className="text-2xl font-extrabold text-slate-900 leading-snug tracking-tight mb-4">
                DECOUVREZ NOS ENGAGEMENTS DANS LA DEMARCHE D'ACCESSIBILITE DE NOS FORMATIONS
              </h2>
            </div>

            <div className="px-8 pb-4">
              <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <span className="text-red-500 font-bold text-lg">!</span>
                    </div>
                    <span className="font-bold text-slate-900">Ressource Handicap Formation</span>
                  </div>
                  <div className="w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center">
                    <span className="text-[10px] text-pink-600 font-bold text-center leading-tight">Engagé l'accessibilité 2024</span>
                  </div>
                </div>

                <p className="text-center font-bold text-slate-800 text-lg mb-3">
                  AFFEA ACADEMIE FRANCOPHONE DE LA FORMATION, DE L'ENSEIGNEMENT ET DE L'ACCOMPAGNEMENT
                </p>
                <p className="text-center text-slate-700 mb-3">
                  est engagé dans la démarche d'accessibilité de ses formations
                </p>
                <p className="text-center text-rose-500 text-sm italic mb-6">
                  Nous mesurons régulièrement le niveau d'accessibilité de nos formations et nous nous engageons sur des actions concrètes d'amélioration.
                </p>

                {/* Engagements */}
                <div className="bg-amber-50/60 rounded-xl p-5 mb-4">
                  <h3 className="text-rose-500 font-semibold mb-3">Nos engagements :</h3>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2"><span className="text-rose-400 font-bold mt-0.5">*</span>sensibiliser et former à la thématique du handicap</li>
                    <li className="flex items-start gap-2"><span className="text-rose-400 font-bold mt-0.5">*</span>identifier et mettre en œuvre les adaptations possibles</li>
                    <li className="flex items-start gap-2"><span className="text-rose-400 font-bold mt-0.5">*</span>organiser les compensations en vue d'améliorer l'accessibilité aux sessions pour les personnes porteuses de handicap</li>
                  </ul>
                </div>

                <div className="flex justify-center my-4">
                  <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-red-400 to-transparent" />
                </div>

                {/* Axes de progrès */}
                <div className="bg-rose-50/60 rounded-xl p-5 mb-4">
                  <h3 className="text-rose-500 font-semibold mb-3">Nos axes de progrès :</h3>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2"><span className="text-rose-400 font-bold mt-0.5">*</span>Améliorer ma démarche d'accueil et d'accompagnement des apprenants en situation de handicap</li>
                    <li className="flex items-start gap-2"><span className="text-rose-400 font-bold mt-0.5">*</span>Rendre accessible pour tous nos informations et contenus pédagogiques</li>
                    <li className="flex items-start gap-2"><span className="text-rose-400 font-bold mt-0.5">*</span>Mieux repérer et identifier les apprenants en situation de handicap largement avant le début de la formation</li>
                  </ul>
                </div>

                {/* Contact */}
                <div className="mt-6">
                  <h3 className="text-rose-500 font-semibold mb-2">Des questions ?</h3>
                  <p className="text-sm text-slate-600 mb-2">Contactez notre référent handicap :</p>
                  <p className="text-sm font-medium text-slate-800">M. NGOUPAYOU Amos</p>
                  <div className="space-y-1 mt-1 text-sm text-slate-600">
                    <p className="flex items-center gap-2">president@affea.fr</p>
                    <p className="flex items-center gap-2">06 03 04 39 13</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══ RIGHT SIDEBAR WIDGETS ═══ */}
        <div className="space-y-4" data-testid="widgets-column">
          {/* Prochain créneau */}
          <Card className="border border-slate-200 shadow-sm rounded-2xl overflow-hidden" data-testid="widget-prochain-creneau">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Prochain créneau</h3>
                  <p className="text-sm text-blue-600">
                    {dashboard?.next_session?.date ? `Le ${dashboard.next_session.date.split('-').reverse().join('/')}` : 'Aucun créneau prévu'}
                  </p>
                </div>
              </div>
              {dashboard?.next_session && (
                <div className="flex items-center gap-3 text-sm text-slate-700">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-slate-400" />
                    <span>à {dashboard.next_session.start_time || '08:00'}</span>
                  </div>
                  <button className="flex items-center gap-1.5 text-blue-600 hover:underline font-medium" data-testid="visio-link">
                    <Video className="h-4 w-4" />Me connecter à la visio
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Évaluations */}
          <Card className="border border-slate-200 shadow-sm rounded-2xl" data-testid="widget-evaluations">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <Star className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Évaluations</h3>
                  <p className="text-sm text-slate-500">
                    {(dashboard?.evaluations?.pending || 0) > 0
                      ? `${dashboard.evaluations.pending} évaluation(s) en attente`
                      : "Vous n'avez pas d'évaluation en attente"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card className="border border-slate-200 shadow-sm rounded-2xl" data-testid="widget-documents">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                    <FolderOpen className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Documents</h3>
                    <p className="text-sm text-blue-600">{dashboard?.documents_count || 0} documents disponibles</p>
                  </div>
                </div>
                <Button
                  className="bg-blue-700 hover:bg-blue-800 text-white text-xs rounded-full px-5"
                  onClick={() => navigate('/apprenant/documents')}
                  data-testid="btn-voir-docs"
                >
                  Voir
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* E-learning */}
          <Card className="border border-slate-200 shadow-sm rounded-2xl" data-testid="widget-elearning">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <Monitor className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900">E-learning</h3>
                </div>
              </div>

              {/* Overall progress bar */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${overallProgress}%` }} />
                </div>
                <span className="text-sm font-semibold text-blue-700">{overallProgress} %</span>
              </div>

              {/* Module progress list */}
              <div className="space-y-3 max-h-44 overflow-y-auto">
                {(dashboard?.modules || []).map((mod) => (
                  <div key={mod.module_id} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-700 flex-1 truncate leading-snug">{mod.title}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-medium text-slate-600">{mod.progress} %</span>
                      {/* Circular progress */}
                      <div className="relative w-7 h-7">
                        <svg className="w-7 h-7 -rotate-90" viewBox="0 0 28 28">
                          <circle cx="14" cy="14" r="11" fill="none" stroke="#e2e8f0" strokeWidth="2.5" />
                          <circle cx="14" cy="14" r="11" fill="none" stroke="#2563eb" strokeWidth="2.5"
                            strokeDasharray={`${(mod.progress / 100) * 69.1} 69.1`} strokeLinecap="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                className="w-full mt-4 bg-blue-700 hover:bg-blue-800 text-white rounded-full"
                onClick={() => navigate('/apprenant/sequences')}
                data-testid="btn-elearning"
              >
                Accéder au e-learning
              </Button>
            </CardContent>
          </Card>

          {/* Émargements */}
          <Card className="border border-slate-200 shadow-sm rounded-2xl" data-testid="widget-emargements">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                    <PenTool className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Émargements</h3>
                    <p className="text-sm text-slate-500">
                      {(dashboard?.emargements?.pending || 0) > 0
                        ? `${dashboard.emargements.pending} signatures en attente`
                        : 'Aucune signature en attente'}
                    </p>
                  </div>
                </div>
                {(dashboard?.emargements?.pending || 0) > 0 && (
                  <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-5"
                    onClick={() => navigate('/apprenant/emargements')} data-testid="btn-signer">
                    Signer
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Règlement intérieur */}
          <Card className="border border-slate-200 shadow-sm rounded-2xl" data-testid="widget-reglement">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                    <Download className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900">Règlement intérieur</h3>
                </div>
                <Button className="bg-blue-700 hover:bg-blue-800 text-white text-xs rounded-full px-5" data-testid="btn-telecharger">
                  Télécharger
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Vos intervenants */}
          <Card className="border border-slate-200 shadow-sm rounded-2xl" data-testid="widget-intervenants">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Vos intervenants</h3>
                  <p className="text-sm text-slate-500">Découvrez vos formateurs</p>
                </div>
              </div>
              <div className="space-y-4">
                {(dashboard?.intervenants || []).map((interv, i) => (
                  <div key={i} className="flex flex-col items-center text-center">
                    <div className="w-24 h-24 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden mb-2">
                      {interv.photo_url ? (
                        <img src={interv.photo_url} alt={interv.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-10 w-10 text-slate-300" />
                      )}
                    </div>
                    <p className="font-bold text-slate-900 text-sm">{(interv.name || '').split(' ').pop()?.toUpperCase()}</p>
                    <p className="text-xs text-slate-500">{(interv.name || '').split(' ').slice(0, -1).join(' ')}</p>
                  </div>
                ))}
                {(!dashboard?.intervenants || dashboard.intervenants.length === 0) && (
                  <p className="text-center text-sm text-slate-400">Aucun intervenant</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Calendrier */}
          <Card className="border border-slate-200 shadow-sm rounded-2xl" data-testid="widget-calendrier">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Calendrier</h3>
                  <p className="text-sm text-slate-500">Vos créneaux de formation</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <Button variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-bold tracking-wide">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </span>
                  <Button variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-7 gap-0 text-center text-xs border border-slate-200 rounded-lg overflow-hidden">
                  {dayNames.map(d => (
                    <div key={d} className="text-slate-500 font-semibold py-2 bg-slate-50 border-b border-slate-200">{d}</div>
                  ))}
                  {getDaysInMonth(currentMonth).map((day, i) => {
                    const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day.date);
                    const hasSession = day.current && sessionDates.includes(dateObj.toDateString());
                    const isToday = day.current &&
                      day.date === new Date().getDate() &&
                      currentMonth.getMonth() === new Date().getMonth() &&
                      currentMonth.getFullYear() === new Date().getFullYear();
                    return (
                      <div key={i} className={`py-2.5 text-sm border-b border-r border-slate-100 ${
                        !day.current ? 'text-slate-300' :
                        hasSession ? 'bg-blue-600 text-white font-bold' :
                        isToday ? 'bg-amber-100 text-amber-800 font-bold' :
                        'text-slate-700'
                      }`}>
                        {day.date}
                      </div>
                    );
                  })}
                </div>

                <Button
                  className="w-full mt-4 bg-blue-700 hover:bg-blue-800 text-white rounded-full"
                  onClick={() => {}} data-testid="btn-voir-calendrier"
                >
                  Voir le calendrier
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </LearnerLayout>
  );
};

export default LearnerDashboard;
